import {
  Badge,
  Box,
  Button,
  Divider,
  GridItem,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { BillingMetricColumnRow } from "@src/components/modules/projects/detail/Stats/billing-box/types";
import {
  ProgressBar,
  type TProgressBarSegment,
} from "@src/components/ui-kit/NewProgressBar";
import { Link } from "@src/shims/chakra-next-js";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { currency } from "@src/utils/formatters";
import type { ReactNode } from "react";

type BillingRowProps = Omit<BillingMetricColumnRow, "name">;
const BillingRow = ({
  label,
  value,
  color,
  onClick,
  href,
}: BillingRowProps) => {
  const isClickable = !!onClick;
  const isLink = !!href;

  return (
    <WrapComponent
      if={isClickable}
      with={(children) => (
        <Button variant="link" onClick={onClick} w="full">
          {children}
        </Button>
      )}
      elseWith={(children) =>
        isLink ? (
          <Link href={href} target="_blank">
            {children}
          </Link>
        ) : (
          children
        )
      }
    >
      <HStack justifyContent="space-between" w="full" fontSize="sm">
        <HStack spacing="2">
          <Box boxSize="2" borderRadius="full" bg={color ?? "gray.400"} />

          <Text color={isClickable || isLink ? "violet.500" : "gray.600"}>
            {label}
          </Text>
        </HStack>

        <Text fontWeight="semibold">{currency.formatByWorkspace(value)}</Text>
      </HStack>
    </WrapComponent>
  );
};

type BillingMetricColumnProps = {
  title: ReactNode;
  count: number;
  total: number;
  rows: BillingMetricColumnRow[];
  segments?: TProgressBarSegment[];
  dashedBefore?: string;
  onHeaderClick?: () => void;
};
export const BillingMetricColumn = ({
  title,
  count,
  total,
  rows,
  segments,
  dashedBefore,
  onHeaderClick,
}: BillingMetricColumnProps) => {
  return (
    <GridItem pt="3" pb="6">
      <VStack align="flex-start" spacing="2">
        <HStack spacing="2" alignItems="center">
          <Badge size="sm" colorScheme="teal" borderRadius="full">
            {count}
          </Badge>

          {onHeaderClick ? (
            <Button
              variant="link"
              onClick={onHeaderClick}
              textTransform="uppercase"
              fontWeight="bold"
              fontSize="xs"
            >
              {title}
            </Button>
          ) : (
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              color="gray.500"
            >
              {title}
            </Text>
          )}
        </HStack>

        <Text fontSize="xl" fontWeight="bold">
          {currency.formatByWorkspace(total)}
        </Text>

        {segments && <ProgressBar size="sm" segments={segments} />}

        <VStack align="flex-start" w="full" spacing="0">
          {rows.map((row) => (
            <Box key={row.label} w="full">
              {dashedBefore === row.label && (
                <Divider variant="dashed" my="1" />
              )}

              <BillingRow
                label={row.label}
                value={row.value}
                color={row.color}
                onClick={row.onClick}
                href={row.href}
              />
            </Box>
          ))}
        </VStack>
      </VStack>
    </GridItem>
  );
};
